import type {
  CreateWorkspacePresetMutation,
  CreateWorkspacePresetMutationVariables,
  UpdateWorkspacePresetMutation,
  UpdateWorkspacePresetMutationVariables,
  SetWorkspaceAutoApplyMutation,
  SetWorkspaceAutoApplyMutationVariables,
  DeleteWorkspacePresetMutation,
  DeleteWorkspacePresetMutationVariables,
} from 'generated/graphql';
import { useMutation, MutationHookOptions } from '@apollo/client';
import { CrossWindowDataRefetchType, NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import {
  GET_WORKSPACES_PREFERENCES,
  CREATE_WINDOW_STATE,
  UPDATE_WINDOW_STATE,
  SET_WORKSPACE_AUTO_APPLY,
  DELETE_WORKSPACE_PRESET,
} from 'modules/Apollo/queries';
import { WithApolloContext } from 'modules/Apollo/types';

export const notifyToolPreferencesUpdate = () => {
  sendCrossTabEvent(NameSpaces.CROSS_WINDOW_DATA_REFETCH, {
    type: CrossWindowDataRefetchType.TOOL_PREFERENCES,
  });
};

export const createMutationOptions = <TData = any, TVariables = any>(
  options?: MutationHookOptions<TData, TVariables>
): MutationHookOptions<TData, TVariables> => ({
  ...options,
  refetchQueries: [{ query: GET_WORKSPACES_PREFERENCES }],
  onCompleted: (data: TData) => {
    options?.onCompleted?.(data);
    notifyToolPreferencesUpdate();
  },
});

export const useWorkspaceMutations = ({ context }: WithApolloContext) => {
  const [createWorkspacePreset] = useMutation<
    CreateWorkspacePresetMutation,
    CreateWorkspacePresetMutationVariables
  >(CREATE_WINDOW_STATE, createMutationOptions({ context }));

  const [updateWorkspacePreset] = useMutation<
    UpdateWorkspacePresetMutation,
    UpdateWorkspacePresetMutationVariables
  >(UPDATE_WINDOW_STATE, createMutationOptions({ context }));

  const [setWorkspaceAutoApply] = useMutation<
    SetWorkspaceAutoApplyMutation,
    SetWorkspaceAutoApplyMutationVariables
  >(SET_WORKSPACE_AUTO_APPLY, createMutationOptions({ context }));

  const [deleteWorkspacePreset] = useMutation<
    DeleteWorkspacePresetMutation,
    DeleteWorkspacePresetMutationVariables
  >(DELETE_WORKSPACE_PRESET, createMutationOptions({ context }));

  return {
    createWorkspacePreset,
    updateWorkspacePreset,
    setWorkspaceAutoApply,
    deleteWorkspacePreset,
  };
};
